import React, { useEffect, useState } from 'react';
import { fetchProspectingQueue } from '../api/monday';
import AddProspectModal from './AddProspectModal';

// ── Design tokens ──────────────────────────────────────────────────────────
const C = {
  indigo:       '#534AB7',
  indigoDark:   '#3C3489',
  indigoLight:  '#EEEDFE',
  teal:         '#1D9E75',
  tealLight:    '#E1F5EE',
  tealDark:     '#085041',
  amber:        '#633806',
  amberLight:   '#FAEEDA',
  amberMid:     '#854F0B',
  red:          '#791F1F',
  redLight:     '#FCEBEB',
  green:        '#27500A',
  greenLight:   '#EAF3DE',
  bgPage:       '#f5f4f1',
  bgCard:       '#ffffff',
  bgSoft:       '#f0efe9',
  border:       '#e8e6e0',
  textPrimary:  '#111827',
  textSecondary:'#6b7280',
  textTertiary: '#9ca3af',
};

// ── Helpers ────────────────────────────────────────────────────────────────

const AVATAR_PALETTE = [
  { bg: C.indigoLight, color: C.indigoDark },
  { bg: C.tealLight,   color: C.tealDark   },
  { bg: C.amberLight,  color: C.amber      },
  { bg: C.greenLight,  color: C.green      },
  { bg: C.redLight,    color: C.red        },
  { bg: '#F3F0FF',     color: '#5B21B6'    },
];

function getAvatarStyle(name) {
  if (!name) return AVATAR_PALETTE[0];
  let h = 0;
  for (let i = 0; i < name.length; i++) h += name.charCodeAt(i);
  return AVATAR_PALETTE[h % AVATAR_PALETTE.length];
}

function getInitials(name) {
  const parts = (name || '').split(/[\s|]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return '?';
}

function getCol(item, colId) {
  return item.column_values?.find((c) => c.id === colId)?.text || '—';
}

function formatDate(dateStr) {
  if (!dateStr || dateStr === '—') return '—';
  const [y, m, d] = dateStr.split('-');
  return new Date(Number(y), Number(m) - 1, Number(d))
    .toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function daysOverdue(dateStr) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const [y, m, d] = dateStr.split('-');
  const due = new Date(Number(y), Number(m) - 1, Number(d));
  return Math.round((today - due) / 86400000);
}

function getTypeTag(prospectType) {
  const t = (prospectType || '').toLowerCase();
  if (t.includes('int requirement'))  return { label: 'inbound',    bg: C.tealLight,   color: C.tealDark  };
  if (t.includes('ext requirement'))  return { label: 'outbound',   bg: C.indigoLight, color: C.indigoDark };
  if (t.includes('off market'))       return { label: 'off market', bg: C.bgSoft,      color: C.textSecondary };
  return { label: prospectType || '—', bg: C.bgSoft, color: C.textSecondary };
}

const PRIORITY_RANK = { 'urgent': 0, 'motivated': 1, 'active': 2, 'watch': 3, 'long term': 4 };

function getPriorityRank(item) {
  const p = (getCol(item, 'priority_1') || '').toLowerCase();
  for (const [key, rank] of Object.entries(PRIORITY_RANK)) {
    if (p.includes(key)) return rank;
  }
  return 5;
}

// Sort by priority tier first, then by follow-up date (ascending)
function sortQueue(items) {
  return [...items].sort((a, b) => {
    const rankDiff = getPriorityRank(a) - getPriorityRank(b);
    if (rankDiff !== 0) return rankDiff;
    const dateA = getCol(a, 'date') || '';
    const dateB = getCol(b, 'date') || '';
    return dateA.localeCompare(dateB);
  });
}

function priorityBorderColor(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('urgent'))    return '#FCA5A5'; // soft red
  if (t.includes('motivated')) return '#F97316'; // clear orange
  if (t.includes('active'))    return '#FACC15'; // clear yellow
  if (t.includes('watch'))     return '#F9A8D4'; // soft pink
  if (t.includes('long term')) return '#93C5FD'; // soft blue
  return '#E5E7EB';
}

function getPriorityTag(text) {
  const t = (text || '').toLowerCase();
  if (t.includes('urgent'))    return { label: 'Urgent',    bg: '#FEE2E2', color: '#991B1B' };
  if (t.includes('motivated')) return { label: 'Motivated', bg: '#FFEDD5', color: '#C2410C' }; // orange
  if (t.includes('active'))    return { label: 'Active',    bg: '#FEF9C3', color: '#A16207' }; // yellow
  if (t.includes('watch'))     return { label: 'Watch',     bg: '#FCE7F3', color: '#831843' };
  if (t.includes('long term')) return { label: 'Long Term', bg: '#DBEAFE', color: '#1E3A8A' };
  return null;
}

// ── Styles ─────────────────────────────────────────────────────────────────

const s = {
  page: {
    fontFamily: "'Inter', 'Segoe UI', system-ui, Arial, sans-serif",
    maxWidth: '720px', margin: '0 auto',
    padding: '0 0 48px', backgroundColor: C.bgPage, minHeight: '100vh',
  },
  header: {
    padding: '20px 18px 16px',
    borderBottom: `1px solid ${C.border}`,
    backgroundColor: C.bgCard,
    marginBottom: '12px',
  },
  headerTop: {
    display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', marginBottom: '12px',
  },
  appTitle: {
    fontSize: '11px', fontWeight: '600', color: C.textTertiary,
    letterSpacing: '0.06em', textTransform: 'uppercase',
  },
  scorecardBtn: {
    background: 'none', border: `1px solid ${C.border}`,
    borderRadius: '8px', padding: '5px 12px',
    fontSize: '12px', fontWeight: '600',
    color: C.textSecondary, cursor: 'pointer',
  },
  brokerRow: {
    display: 'flex', alignItems: 'baseline',
    justifyContent: 'space-between', marginBottom: '4px',
  },
  brokerName: { fontSize: '20px', fontWeight: '700', color: C.textPrimary, margin: 0 },
  switchBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '12px', color: C.textTertiary,
    padding: 0, textDecoration: 'underline',
  },
  subtitle: { fontSize: '13px', color: C.textSecondary },

  // Stat tiles
  statRow: { display: 'flex', gap: '8px', marginTop: '14px' },
  statTile: {
    flex: 1, borderRadius: '10px', padding: '10px 12px', textAlign: 'center',
  },
  statNum: { fontSize: '20px', fontWeight: '700', lineHeight: 1, marginBottom: '3px' },
  statLabel: { fontSize: '10px', fontWeight: '600', letterSpacing: '0.04em' },

  // Sections
  section: { padding: '0 12px', marginBottom: '4px' },
  sectionHeader: {
    display: 'flex', alignItems: 'center', gap: '8px',
    padding: '12px 6px 6px',
  },
  sectionLabel: {
    fontSize: '11px', fontWeight: '700',
    letterSpacing: '0.08em', textTransform: 'uppercase',
  },
  sectionBadge: {
    fontSize: '11px', fontWeight: '700',
    borderRadius: '99px', padding: '1px 8px',
  },
  collapseBtn: {
    background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: C.textTertiary,
    padding: '0', lineHeight: 1, marginLeft: 'auto',
  },

  // Cards
  card: {
    backgroundColor: C.bgCard, borderRadius: '12px',
    padding: '14px 16px', marginBottom: '6px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
    cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: '12px',
  },
  avatar: {
    width: '38px', height: '38px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '13px', fontWeight: '600', flexShrink: 0,
  },
  cardContent: { flex: 1, minWidth: 0 },
  cardName: { fontSize: '14px', fontWeight: '600', color: C.textPrimary, margin: '0 0 2px' },
  cardMeta: { fontSize: '11px', color: C.textTertiary, margin: '0 0 5px' },
  tag: {
    display: 'inline-block', fontSize: '11px', fontWeight: '500',
    padding: '2px 8px', borderRadius: '10px',
  },

  // Empty + loading + error
  emptyWrap: { padding: '12px' },
  emptyCard: {
    textAlign: 'center', padding: '48px 20px',
    backgroundColor: C.bgCard, borderRadius: '14px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
  },
  emptyIcon:  { fontSize: '36px', marginBottom: '10px' },
  emptyTitle: { fontSize: '17px', fontWeight: '700', color: C.textPrimary, margin: '0 0 6px' },
  emptyText:  { fontSize: '13px', color: C.textTertiary },
  loading: { textAlign: 'center', padding: '60px 20px', color: C.textTertiary, fontSize: '15px' },
  error:   { margin: '16px', background: C.redLight, border: `1px solid #FECACA`, borderRadius: '10px', padding: '14px', color: C.red, fontSize: '13px' },
};

// ── Sub-components ─────────────────────────────────────────────────────────

function ProspectCard({ item, badgeType, onClick }) {
  const priority     = getCol(item, 'priority_1');
  const followUp     = getCol(item, 'date');
  const prospectType = getCol(item, 'status1');

  const contactName  = item.name.split('|')[0].trim();
  const avatarStyle  = getAvatarStyle(contactName);
  const initials     = getInitials(contactName);
  const borderColor  = priorityBorderColor(priority);
  const typeTag      = getTypeTag(prospectType);
  const priorityTag  = getPriorityTag(priority);

  // Meta line
  let metaLine = '';
  if (badgeType === 'overdue') {
    const d = daysOverdue(followUp);
    metaLine = `${d} day${d !== 1 ? 's' : ''} overdue`;
  } else if (badgeType === 'today') {
    metaLine = 'Due today';
  } else {
    metaLine = formatDate(followUp);
  }

  // Tag override for overdue
  const displayTag = badgeType === 'overdue'
    ? { label: 'overdue', bg: C.amberLight, color: C.amber }
    : typeTag;

  return (
    <div style={{ ...s.card, borderLeft: `5px solid ${borderColor}` }} onClick={onClick}>
      <div style={{ ...s.avatar, backgroundColor: avatarStyle.bg, color: avatarStyle.color }}>
        {initials}
      </div>
      <div style={s.cardContent}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '2px' }}>
          <p style={{ ...s.cardName, marginBottom: 0 }}>{contactName}</p>
          {priorityTag && (
            <span style={{
              fontSize: '10px', fontWeight: '700',
              padding: '2px 7px', borderRadius: '5px',
              backgroundColor: priorityTag.bg, color: priorityTag.color,
              letterSpacing: '0.03em', flexShrink: 0, marginLeft: '8px',
            }}>
              {priorityTag.label}
            </span>
          )}
        </div>
        <p style={s.cardMeta}>{metaLine}</p>
        <span style={{ ...s.tag, backgroundColor: displayTag.bg, color: displayTag.color }}>
          {displayTag.label}
        </span>
      </div>
    </div>
  );
}

function QueueSection({ label, labelColor, items = [], badgeType, badgeBg, badgeColor, defaultCollapsed = false, onSelectItem }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  if (!Array.isArray(items) || items.length === 0) return null;

  return (
    <div style={s.section}>
      <div
        style={{ ...s.sectionHeader, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => setCollapsed((v) => !v)}
      >
        <span style={{ ...s.sectionLabel, color: labelColor }}>{label}</span>
        <span style={{ ...s.sectionBadge, backgroundColor: badgeBg, color: badgeColor }}>
          {items.length}
        </span>
        <button
          style={{ ...s.collapseBtn, transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)' }}
          onClick={(e) => { e.stopPropagation(); setCollapsed((v) => !v); }}
        >
          ▾
        </button>
      </div>
      {!collapsed && items.map((item) => (
        <ProspectCard key={item.id} item={item} badgeType={badgeType} onClick={() => onSelectItem(item)} />
      ))}
    </div>
  );
}

// ── Cache helpers ──────────────────────────────────────────────────────────

const QUEUE_CACHE_KEY = (broker) => `sterling_queue_v1_${broker}`;

function readQueueCache(broker) {
  try {
    const raw = localStorage.getItem(QUEUE_CACHE_KEY(broker));
    return raw ? JSON.parse(raw).data : null;
  } catch { return null; }
}

function writeQueueCache(broker, data) {
  try { localStorage.setItem(QUEUE_CACHE_KEY(broker), JSON.stringify({ data, ts: Date.now() })); } catch {}
}

// ── Main component ─────────────────────────────────────────────────────────

export default function Queue({ brokerName = 'Matt Mellott', onSelectItem, skippedIds = new Set(), onOpenScorecard, onSwitchBroker }) {
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]         = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [search, setSearch]       = useState('');

  useEffect(() => {
    // Show cached data immediately so the broker isn't staring at a spinner
    const cached = readQueueCache(brokerName);
    if (cached) {
      setData(cached);
      setLoading(false);
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    // Always fetch fresh in the background
    fetchProspectingQueue(brokerName)
      .then((d) => {
        setData(d);
        setLoading(false);
        setRefreshing(false);
        writeQueueCache(brokerName, d);
      })
      .catch((err) => {
        // If we have cached data, don't show an error — just silently stop refreshing
        if (!cached) setError(err.message || 'Failed to load queue.');
        setLoading(false);
        setRefreshing(false);
      });
  }, [brokerName, refreshKey]); // eslint-disable-line

  if (loading) return <div style={s.page}><div style={s.loading}>Loading your queue…</div></div>;
  if (error)   return <div style={s.page}><div style={s.error}><strong>Error:</strong> {error}</div></div>;

  const skip = (items) => (items || []).filter((item) => !skippedIds.has(item.id));
  const q = search.trim().toLowerCase();
  const filterBySearch = (items) => q
    ? items.filter((item) => item.name.toLowerCase().includes(q))
    : items;
  const overdue   = filterBySearch(sortQueue(skip(data.overdue)));
  const dueToday  = filterBySearch(sortQueue(skip(data.dueToday)));
  const comingUp  = filterBySearch(sortQueue(skip(data.comingUp)));

  const actionableCount = overdue.length + dueToday.length;
  const skippedCount    = skippedIds.size;

  const today = new Date().toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });

  return (
    <div style={s.page}>
      {showAddModal && (
        <AddProspectModal
          brokerName={brokerName}
          onClose={() => setShowAddModal(false)}
          onAdded={() => { setShowAddModal(false); setRefreshKey((k) => k + 1); }}
        />
      )}

      {/* Floating + button */}
      <button
        onClick={() => setShowAddModal(true)}
        title="Add contact or prospect"
        style={{
          position: 'fixed', bottom: '28px', right: '20px', zIndex: 900,
          width: '54px', height: '54px', borderRadius: '50%',
          backgroundColor: C.indigo, color: '#fff',
          border: 'none', fontSize: '26px', lineHeight: 1,
          cursor: 'pointer', boxShadow: '0 4px 16px rgba(83,74,183,0.45)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
      >
        +
      </button>

      {/* Header */}
      <div style={s.header}>
        <div style={s.headerTop}>
          <p style={s.appTitle}>SterlingCRE — Prospect + Nurture</p>
          <button style={s.scorecardBtn} onClick={onOpenScorecard}>Scorecard</button>
        </div>

        <div style={s.brokerRow}>
          <h1 style={s.brokerName}>{brokerName.split(' ')[0]}'s queue</h1>
          {onSwitchBroker && (
            <button style={s.switchBtn} onClick={onSwitchBroker}>switch</button>
          )}
        </div>
        <p style={s.subtitle}>
          {today}
          {refreshing && <span style={{ marginLeft: '8px', fontSize: '11px', color: C.textTertiary }}>↻ syncing…</span>}
        </p>

        {/* Stat tiles */}
        <div style={s.statRow}>
          <div style={{ ...s.statTile, backgroundColor: C.bgSoft }}>
            <div style={{ ...s.statNum, color: C.textPrimary }}>{dueToday.length + overdue.length}</div>
            <div style={{ ...s.statLabel, color: C.textSecondary }}>due today</div>
          </div>
          {overdue.length > 0 && (
            <div style={{ ...s.statTile, backgroundColor: C.amberLight }}>
              <div style={{ ...s.statNum, color: C.amber }}>{overdue.length}</div>
              <div style={{ ...s.statLabel, color: C.amberMid }}>overdue</div>
            </div>
          )}
          {comingUp.length > 0 && (
            <div style={{ ...s.statTile, backgroundColor: C.bgSoft }}>
              <div style={{ ...s.statNum, color: C.textSecondary }}>{comingUp.length}</div>
              <div style={{ ...s.statLabel, color: C.textTertiary }}>coming up</div>
            </div>
          )}
        </div>
      </div>

      {/* Search bar */}
      <div style={{ padding: '10px 12px 0' }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', fontSize: '14px', color: C.textTertiary, pointerEvents: 'none' }}>🔍</span>
          <input
            type="text"
            placeholder="Search contacts…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '10px 12px 10px 36px',
              border: `1.5px solid ${C.border}`, borderRadius: '10px',
              fontSize: '14px', color: C.textPrimary,
              backgroundColor: C.bgCard, fontFamily: 'inherit', outline: 'none',
            }}
          />
          {search && (
            <button onClick={() => setSearch('')} style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: C.textTertiary, lineHeight: 1 }}>×</button>
          )}
        </div>
      </div>

      {actionableCount === 0 && !search ? (
        <>
          <div style={s.emptyWrap}>
            <div style={s.emptyCard}>
              <div style={s.emptyIcon}>✅</div>
              <h2 style={s.emptyTitle}>You're all caught up!</h2>
              <p style={s.emptyText}>
                {skippedCount > 0
                  ? `${skippedCount} skipped contact${skippedCount !== 1 ? 's' : ''} will reappear tomorrow.`
                  : comingUp.length > 0
                    ? `${comingUp.length} more coming up in the next few days.`
                    : 'No contacts due. Enjoy the day.'}
              </p>
            </div>
          </div>
          {comingUp.length > 0 && (
            <QueueSection
              label="Coming Up" labelColor={C.textSecondary}
              items={comingUp} badgeType="upcoming"
              badgeBg={C.bgSoft} badgeColor={C.textSecondary}
              defaultCollapsed={true} onSelectItem={onSelectItem}
            />
          )}
        </>
      ) : (
        <>
          <QueueSection
            label="Due Today" labelColor={C.indigoDark}
            items={dueToday} badgeType="today"
            badgeBg={C.indigoLight} badgeColor={C.indigoDark}
            onSelectItem={onSelectItem}
          />
          <QueueSection
            label="Overdue" labelColor="#B45309"
            items={overdue} badgeType="overdue"
            badgeBg={C.amberLight} badgeColor={C.amber}
            onSelectItem={onSelectItem}
          />
          <QueueSection
            label="Coming Up" labelColor={C.textSecondary}
            items={comingUp} badgeType="upcoming"
            badgeBg={C.bgSoft} badgeColor={C.textSecondary}
            defaultCollapsed={true} onSelectItem={onSelectItem}
          />
        </>
      )}
    </div>
  );
}
